import React, { useState, useEffect, useRef } from 'react';
import './AnimatedBanner.css';

const AnimatedBanner = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const bannerRef = useRef(null);

  const slides = [
    {
      type: 'image',
      content: {
        icon: '📱',
        title: 'CableBiz Customer App',
        description: 'Manage your cable TV subscription with ease'
      }
    },
    {
      type: 'content',
      content: {
        title: 'Plan Management',
        description: 'View and switch between cable TV plans effortlessly. Choose the perfect package for your needs.',
        icon: '📋'
      }
    },
    {
      type: 'image',
      content: {
        icon: '💳',
        title: 'Online Payment',
        description: 'Secure and convenient bill payment'
      }
    },
    {
      type: 'content',
      content: {
        title: 'Complain Tracker',
        description: 'Track your service requests in real-time. Get instant updates on complaint resolution.',
        icon: '📞'
      }
    },
    {
      type: 'content',
      content: {
        title: 'Payment Received',
        description: 'Instant payment confirmation with digital receipts. Keep track of all your transactions.',
        icon: '✅'
      }
    },
    {
      type: 'image',
      content: {
        icon: '🚀',
        title: 'Collection Agent App',
        description: 'Streamline collection operations'
      }
    }
  ];

  useEffect(() => {
    // Intersection Observer for scroll animation
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (bannerRef.current) {
      observer.observe(bannerRef.current);
    }

    // Auto-scroll slides
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000); // Change slide every 4 seconds

    return () => {
      clearInterval(interval);
      if (bannerRef.current) {
        observer.unobserve(bannerRef.current);
      }
    };
  }, [slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <section className={`animated-banner ${isVisible ? 'visible' : ''}`} ref={bannerRef}>
      <div className="banner-container">
        <div className="banner-wrapper">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`banner-slide ${index === currentSlide ? 'active' : ''} ${
                index < currentSlide ? 'prev' : index > currentSlide ? 'next' : ''
              }`}
              style={{
                transform: `translateX(${(index - currentSlide) * 100}%)`,
              }}
            >
              {slide.type === 'image' ? (
                <div className="slide-image">
                  <div className="image-content">
                    <div className="image-icon">{slide.content.icon}</div>
                    <h3>{slide.content.title}</h3>
                    <p>{slide.content.description}</p>
                  </div>
                </div>
              ) : (
                <div className="slide-content">
                  <div className="content-icon">{slide.content.icon}</div>
                  <h3>{slide.content.title}</h3>
                  <p>{slide.content.description}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        <button className="banner-nav prev-btn" onClick={prevSlide} aria-label="Previous slide">
          ‹
        </button>
        <button className="banner-nav next-btn" onClick={nextSlide} aria-label="Next slide">
          ›
        </button>

        {/* Dots Indicator */}
        <div className="banner-dots">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default AnimatedBanner;

